import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMenu, FiSettings, FiPlusCircle, FiBell } from 'react-icons/fi';
import { RiTokenSwapFill } from 'react-icons/ri';
import NotificationDialog from './NotificationDialog';
import { useWallet } from '../context/WalletContext';
import { useChain } from '../context/ChainContext';
import { MAIN_ASSETS, POPULAR_BEP20_TOKENS } from '../constants/tokens';
import '../styles/Header.css';
import TokenIcon from './TokenIcon';
import dexLogo from '../assests/DexWallet Logo Symbol.png';

const Header = ({ isHome = false, title = '', onMenuClick, className = '' }) => {
  const navigate = useNavigate();
  const { wallet, transactions, tokenTransactions } = useWallet();
  const { selectedChain, isTestnet, toggleNetwork } = useChain();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [lastTxCount, setLastTxCount] = useState(0);
  const [lastTokenTxCount, setLastTokenTxCount] = useState(0);
  const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);

  useEffect(() => {
    const processNewTransactions = () => {
      if (!wallet?.address) return;

      // Process native token transactions
      if (transactions && transactions.length > lastTxCount) {
        const newTxs = transactions.slice(0, transactions.length - lastTxCount);
        newTxs.forEach(tx => {
          const isIncoming = tx.in_msg?.destination === wallet.address;
          const amount = tx.in_msg?.value || '0';
          const notification = {
            id: tx.hash,
            type: isIncoming ? 'receive' : 'send',
            amount: amount,
            timestamp: Date.now(),
            from: tx.in_msg?.source,
            to: tx.in_msg?.destination,
            symbol: selectedChain
          };
          setNotifications(prev => [notification, ...prev].slice(0, 10));
        });
        setLastTxCount(transactions.length);
      }

      // Process token transactions
      if (tokenTransactions && tokenTransactions.length > lastTokenTxCount) {
        const newTokenTxs = tokenTransactions.slice(0, tokenTransactions.length - lastTokenTxCount);
        newTokenTxs.forEach(tx => {
          const isIncoming = tx.to === wallet.address;
          const notification = {
            id: tx.hash,
            type: isIncoming ? 'receive' : 'send',
            amount: tx.amount,
            timestamp: Date.now(),
            from: tx.from,
            to: tx.to,
            tokenAddress: tx.tokenAddress,
            symbol: tx.symbol || getTokenSymbol(tx.tokenAddress)
          };
          setNotifications(prev => [notification, ...prev].slice(0, 10));
        });
        setLastTokenTxCount(tokenTransactions.length);
      }
    };

    processNewTransactions();
  }, [wallet?.address, transactions, tokenTransactions, lastTxCount, lastTokenTxCount, selectedChain]);

  const handleNetworkToggle = async () => {
    if (isSwitchingNetwork) return;

    try {
      setIsSwitchingNetwork(true);
      await toggleNetwork();
    } catch (error) {
      console.error('Failed to toggle network:', error);
    } finally {
      setIsSwitchingNetwork(false);
    }
  };

  const getTokenSymbol = (tokenAddress) => {
    if (!tokenAddress) return selectedChain;

    const network = isTestnet ? 'testnet' : 'mainnet';
    const allTokens = POPULAR_BEP20_TOKENS[network];
    const token = allTokens.find(t => t.address.toLowerCase() === tokenAddress.toLowerCase());
    return token ? token.symbol : 'Unknown Token';
  };

  const formatAmount = (amount, decimals = 18) => {
    try {
      const num = parseFloat(amount) / Math.pow(10, decimals);
      return num.toFixed(num >= 1 ? 2 : 4);
    } catch (error) {
      console.error('Error formatting amount:', error);
      return '0.00';
    }
  };

  if (isHome) {
    return (
      <motion.header
        className={`header home-header ${className}`}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="header-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <motion.button
            className="header-button"
            onClick={onMenuClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isSwitchingNetwork}
          >
            <FiMenu />
          </motion.button>

          {/* Centered DexWallet logo and name */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <motion.span
              className="rigo-wallet-brand"
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.18, duration: 0.5, type: 'spring', stiffness: 80 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                userSelect: 'none',
              }}
            >
              <img
                src={dexLogo}
                alt="DexWallet Logo"
                style={{ width: 28, height: 28, borderRadius: 8, boxShadow: '0 2px 8px rgba(67,233,123,0.10)' }}
                draggable={false}
              />
              <span
                style={{
                  fontWeight: 800,
                  fontSize: 18,
                  letterSpacing: '-0.5px',
                  fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
                  color: '#fff', // Use plain white
                  textShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  display: 'inline-block',
                  lineHeight: 1.1,
                }}
              >
                DexWallet
              </span>
            </motion.span>
          </div>

          {/* Header actions: network toggle and others on the right */}
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <motion.button
              className={`network-toggle-button ${isSwitchingNetwork ? 'switching' : ''}`}
              onClick={handleNetworkToggle}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              whileHover={{ scale: isSwitchingNetwork ? 1 : 1.02 }}
              whileTap={{ scale: isSwitchingNetwork ? 1 : 0.98 }}
              title={`Switch to ${isTestnet ? 'Mainnet' : 'Testnet'}`}
              disabled={isSwitchingNetwork}
            >
              <span className="network-toggle-text">
                {isSwitchingNetwork ? 'Switching...' : (isTestnet ? 'Testnet' : 'Mainnet')}
              </span>
            </motion.button>

          </div>
        </div>
        <NotificationDialog
          isOpen={isDialogOpen}
          onClose={() => setDialogOpen(false)}
          notifications={notifications}
        />
      </motion.header>
    );
  }

  return (
    <motion.header
      className={`header ${className}`}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="header-content">
        <motion.button
          className="header-button"
          onClick={() => !isSwitchingNetwork && navigate(-1)}
          whileHover={{ scale: isSwitchingNetwork ? 1 : 1.05 }}
          whileTap={{ scale: isSwitchingNetwork ? 1 : 0.95 }}
          disabled={isSwitchingNetwork}
        >
          <FiArrowLeft className={isSwitchingNetwork ? 'disabled' : ''} />
        </motion.button>

        <motion.h1
          className="header-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {isSwitchingNetwork ? 'Switching Network...' : title}
        </motion.h1>

        <div className="notification-container">
          <motion.button
            className="notification-button"
            onClick={() => !isSwitchingNetwork && setDialogOpen(true)}
            whileHover={{ scale: isSwitchingNetwork ? 1 : 1.1 }}
            whileTap={{ scale: isSwitchingNetwork ? 1 : 0.9 }}
            disabled={isSwitchingNetwork}
          >
            <FiBell className={isSwitchingNetwork ? 'disabled' : ''} />
            {notifications.length > 0 && (
              <motion.div
                className="notification-badge"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              >
                {notifications.length}
              </motion.div>
            )}
          </motion.button>
        </div>
      </div>
      <NotificationDialog
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        notifications={notifications}
      />
    </motion.header>
  );
};

export default Header;
